import type { ChartMilestone } from './types'

/**
 * Which side of its line a milestone's label is drawn on.
 *
 * ECharts draws it to the left, always. A milestone at the start of the axis
 * then puts its label off the canvas — the first plan to run dry in its first
 * year showed "t of Money" where "Out of Money" belonged.
 */
export type LabelSide = 'left' | 'right'

export type LabelledMilestone = ChartMilestone & {
  labelSide?: LabelSide
  /**
   * Which row of labels this one sits in, counted from the top.
   *
   * Absent is the top row, which is where every label sits until one cannot be
   * fitted there — see `withLabelPlacement`.
   */
  labelRow?: number
}

/**
 * How far along the axis a label still needs the room on its right.
 *
 * A fraction, because neither the grid's width nor the text's is known here.
 * A label runs about a quarter of a phone's plot, and it may overhang the
 * axis gutter, so the first sixth is where the two collide.
 */
const FLIP_BEFORE = 1 / 6

/**
 * How much of the axis a label takes, as a fraction of its length. Generous on
 * purpose: labels moved apart when they would have fitted read as a layout,
 * where two that overlap read as a fault.
 */
const LABEL_SPAN = 1 / 4

type Span = [start: number, end: number]

const positionOf = (
  milestone: ChartMilestone,
  ages: string[]
): number | undefined => {
  const index = ages.indexOf(milestone.age)
  if (index === -1 || ages.length < 2) return undefined
  return index / (ages.length - 1)
}

/** The side a label takes when nothing is in its way. */
const preferredSide = (position: number): LabelSide =>
  position > FLIP_BEFORE ? 'left' : 'right'

const otherSide = (side: LabelSide): LabelSide =>
  side === 'left' ? 'right' : 'left'

const spanOf = (position: number, side: LabelSide): Span =>
  side === 'right'
    ? [position, position + LABEL_SPAN]
    : [position - LABEL_SPAN, position]

const fitsOnCanvas = ([start, end]: Span): boolean => start >= 0 && end <= 1

const overlaps = (a: Span, b: Span): boolean => a[0] < b[1] && b[0] < a[1]

type Placement = { side: LabelSide; row: number }

/**
 * The first place this label fits: its own side of the line, then the other
 * side, then the same two a row down.
 *
 * Flipping before dropping, because a label beside its line still reads as
 * belonging to it. The flip is taken only where the turned label stays on the
 * canvas, which is what `FLIP_BEFORE` guards.
 */
const placeIn = (rows: Span[][], position: number): Placement => {
  const wanted = preferredSide(position)
  const sides: LabelSide[] = [wanted, otherSide(wanted)]

  for (let row = 0; row <= rows.length; row += 1) {
    const taken = rows[row] ?? []
    for (const side of sides) {
      const span = spanOf(position, side)
      if (side !== wanted && !fitsOnCanvas(span)) continue
      if (taken.some(other => overlaps(other, span))) continue
      rows[row] = [...taken, span]
      return { side, row }
    }
  }

  // Unreachable: the loop runs one past the last row, and an empty row takes
  // any label. Here so the return type needs no widening.
  return { side: wanted, row: rows.length }
}

/**
 * Where each milestone's label is drawn: which side of its line, and which row.
 *
 * Left to right, each label taking the first place it does not touch another.
 * Nothing moves while nothing collides: a lone label is drawn exactly where
 * ECharts would have drawn it.
 *
 * Milestones at the same age are not handled here. They are merged into one
 * label before they are drawn, and one label reads better than two stacked on a
 * single line.
 *
 * Order is preserved, because a caller's milestones are its own.
 */
export const withLabelPlacement = (
  milestones: ChartMilestone[],
  ages: string[]
): LabelledMilestone[] => {
  const rows: Span[][] = []
  const placements = new Map<ChartMilestone, Placement>()

  for (const { milestone, position } of milestones
    .map(milestone => ({ milestone, position: positionOf(milestone, ages) }))
    .filter(entry => entry.position !== undefined)
    .sort((left, right) => left.position! - right.position!)) {
    placements.set(milestone, placeIn(rows, position!))
  }

  return milestones.map(milestone => {
    const placement = placements.get(milestone)
    if (!placement) return milestone

    return {
      ...milestone,
      ...(placement.side === 'right' ? { labelSide: 'right' as const } : {}),
      ...(placement.row > 0 ? { labelRow: placement.row } : {}),
    }
  })
}
